import * as Sentry from "@sentry/capacitor";
import assertNever from "assert-never";
import * as React from "react";
import { ReactElement } from "react";
import { TrainingCenterBundleItemVideoEngmtQuestionId } from "../../messages";
import {
  TrainingCenterBundleItemVideoDimensions,
  TrainingCenterCaregiverVideoBundleItemWatchSegmentParams,
  TrainingCenterTranslated,
  TrainingCenterVideoSubtitles,
} from "../../messages/TrainingCenter";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItemVideoEngagementQuestion,
  TrainingCenterBundleItemVideoMediaOverlay,
} from "../../system/AppState/AppStateTrainingCenter";
import { HashMap } from "../../utils/HashMap";
import { HashSet } from "../../utils/HashSet";
import { List } from "../../utils/List";
import { TrainingCenterLanguagePreference } from "./TrainingCenterLanguageLookup";
import {
  makeTrainingCenterVideoMediaItemKey,
  TrainingCenterVideoPlayer,
} from "./TrainingCenterVideoPlayer";
import { generateClientUniqueId } from "./TrainingCenterVideoPlayer/Utils";
import { VideoPlayerEvent } from "./VideoPlayerEventsNormalizer";

export interface TrainingCenterVideoMediaItemProps<Userdata> {
  userdata: Userdata;
  videoUrl: string;
  videoThumbnailUrl: string | null;
  videoLengthSeconds: number;
  videoDimensions: TrainingCenterBundleItemVideoDimensions;
  videoSubtitles: List<TrainingCenterTranslated<TrainingCenterVideoSubtitles>>;
  engagementQuestions: List<TrainingCenterBundleItemVideoEngagementQuestion> | null;
  mediaOverlays: List<TrainingCenterBundleItemVideoMediaOverlay> | null;
  languagePreference: TrainingCenterLanguagePreference;
  chosenEngagementAnswers: HashMap<
    TrainingCenterBundleItemVideoEngmtQuestionId,
    EngagementChosenAnswer
  >;
  closedEngagementQuestions: HashSet<TrainingCenterBundleItemVideoEngmtQuestionId>;
  playing: boolean;
  isFullscreen: boolean;
  videoAspectRatio: number;
  onSetPlaying: (playing: boolean) => void;
  onClickCloseAnswer: (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => void;
  onClickEngagementAnswer: (
    question: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    currentSecond: number
  ) => void;
  watchedSegments: [number, number][];

  onWatchSegment: (
    userdata: Userdata,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
  ) => void;
  onChangeVideoAspectRatio: (aspectRatio: number) => void;
  onChangeFullscreen: (isFullScreen: boolean) => void;
}

export interface TrainingCenterVideoMediaItemMethods {
  getCurrentSeconds(): number;
}

const WATCH_SEGMENT_SECONDS_INTERVAL = 5;

const TrainingCenterVideoMediaItemImpl = function TrainingCenterVideoMediaItem<Userdata>(
  props: TrainingCenterVideoMediaItemProps<Userdata>,
  ref: React.Ref<TrainingCenterVideoMediaItemMethods>
): ReactElement {
  React.useImperativeHandle(
    ref,
    (): TrainingCenterVideoMediaItemMethods => ({
      getCurrentSeconds: (): number => {
        return Math.floor(currentSeconds.current);
      },
    }),
    []
  );

  const clientUniqueId = React.useRef(generateClientUniqueId());
  const playing = React.useRef(false);
  const startSeconds = React.useRef(0);
  const prevEndSeconds = React.useRef(0);
  const currentSeconds = React.useRef(0);
  const [showVideoLoading, setVideoLoading] = React.useState(true);

  const emitWatchSegment = React.useCallback((): number => {
    const endSeconds = Math.floor(currentSeconds.current);
    props.onWatchSegment.call(null, props.userdata, clientUniqueId.current, {
      startSeconds: startSeconds.current,
      endSeconds: endSeconds,
    });
    return endSeconds;
  }, [props.onWatchSegment, props.userdata]);

  // If the user exits the video, then we want to send the latest watched
  // time
  React.useEffect(() => {
    return () => {
      if (playing.current) {
        emitWatchSegment();
      }
    };
  }, [emitWatchSegment]);

  const handleVideoPlayerReset = React.useCallback(() => {
    if (playing.current) {
      emitWatchSegment();
    }

    playing.current = false;
    startSeconds.current = 0;
    prevEndSeconds.current = 0;
    clientUniqueId.current = generateClientUniqueId();
  }, [emitWatchSegment]);

  const handleVideoPlayerEvent = React.useCallback(
    (event: VideoPlayerEvent) => {
      switch (event.type) {
        case "Play": {
          playing.current = true;
          startSeconds.current = Math.floor(currentSeconds.current);
          prevEndSeconds.current = Math.floor(currentSeconds.current);
          clientUniqueId.current = generateClientUniqueId();
          break;
        }
        case "Pause": {
          playing.current = false;
          emitWatchSegment();
          break;
        }
        case "Seek": {
          if (playing.current) {
            const endSeconds = Math.floor(currentSeconds.current);
            // When in "play" mode and dragging the seek bar, we don't want to
            // send lots of 0 second watch segments
            if (endSeconds - startSeconds.current > 1) {
              emitWatchSegment();
            }
          }
          setVideoLoading(true);
          currentSeconds.current = event.seconds;
          startSeconds.current = Math.floor(event.seconds);
          prevEndSeconds.current = Math.floor(event.seconds);
          clientUniqueId.current = generateClientUniqueId();
          break;
        }
        case "Progress": {
          setVideoLoading(false);
          currentSeconds.current = event.seconds;
          if (currentSeconds.current - prevEndSeconds.current >= WATCH_SEGMENT_SECONDS_INTERVAL) {
            const endSeconds = emitWatchSegment();
            prevEndSeconds.current = endSeconds;
          }
          break;
        }
        default:
          assertNever(event);
      }
    },
    [emitWatchSegment]
  );

  const handleVideoPlayerError = React.useCallback((error: any, data?: any) => {
    console.log("ERROR in ReactPlayer:", data);
    Sentry.captureException(error);
  }, []);

  return (
    <TrainingCenterVideoPlayer
      key={makeTrainingCenterVideoMediaItemKey(props.videoUrl, props.videoLengthSeconds)}
      videoUrl={props.videoUrl}
      videoThumbnailUrl={props.videoThumbnailUrl}
      videoLengthSeconds={props.videoLengthSeconds}
      videoDimensions={props.videoDimensions}
      videoSubtitles={props.videoSubtitles}
      watchedSegments={props.watchedSegments}
      engagementQuestions={props.engagementQuestions}
      mediaOverlays={props.mediaOverlays}
      chosenEngagementAnswers={props.chosenEngagementAnswers}
      closedEngagementQuestions={props.closedEngagementQuestions}
      languagePreference={props.languagePreference}
      playing={props.playing}
      fullscreen={props.isFullscreen}
      aspectRatio={props.videoAspectRatio}
      showVideoLoading={showVideoLoading}
      onSetPlaying={props.onSetPlaying}
      onVideoLoadingChange={setVideoLoading}
      onClickEngagementAnswer={props.onClickEngagementAnswer}
      onClickCloseAnswer={props.onClickCloseAnswer}
      onVideoPlayerReset={handleVideoPlayerReset}
      onVideoPlayerEvent={handleVideoPlayerEvent}
      onVideoPlayerError={handleVideoPlayerError}
      onChangeVideoAspectRatio={props.onChangeVideoAspectRatio}
      onChangeFullscreen={props.onChangeFullscreen}
    />
  );
};

// TODO Fix any (Benny)
export const TrainingCenterVideoMediaItem = React.memo(
  React.forwardRef(TrainingCenterVideoMediaItemImpl)
) as any;
